// src/pages/admin/CourseLectures/lectures/Lecture02Lebenslauf.tsx
import React, { useState, useRef } from "react";
import {
  Checklist,
  InfoBox,
  AgendaCard,
  TaskList,
} from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Local sub-components ──────────────────────────────────────────
const StatCard: React.FC<{
  value: string;
  label: string;
  icon: string;
  color: string;
}> = ({ value, label, icon, color }) => (
  <div className={`rounded-xl border p-2 sm:p-4 text-center ${color}`}>
    <div className="text-lg sm:text-2xl mb-0.5">{icon}</div>
    <div className="text-base sm:text-2xl font-extrabold leading-tight">
      {value}
    </div>
    <div className="text-[10px] sm:text-xs font-medium mt-1 opacity-80 leading-tight">
      {label}
    </div>
  </div>
);

const DoItem: React.FC<{ children: React.ReactNode; type: "do" | "dont" }> = ({
  children,
  type,
}) => (
  <li className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0 text-sm">
    <span
      className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${type === "do" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}
    >
      {type === "do" ? "✓" : "✕"}
    </span>
    <span className={type === "dont" ? "text-gray-500" : "text-gray-700"}>
      {children}
    </span>
  </li>
);

const CVSection: React.FC<{
  title: string;
  left: string;
  children: React.ReactNode;
}> = ({ title, left, children }) => (
  <div className="border-b border-gray-100 last:border-0 py-2.5">
    <span className="block text-[11px] font-bold text-[#009fe3] uppercase tracking-wide mb-0.5">
      {left}
    </span>
    <div className="sm:pl-4 sm:border-l-2 sm:border-[#009fe3]">
      <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
      <div className="text-sm text-gray-600">{children}</div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture02Lebenslauf: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 01 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {[
            "Zeugnisse übersetzen",
            "Zeugnisse organisieren",
            "Bewerbungsfoto",
            "Professionelle E-Mail",
            "Liste mit 10 Berufen",
            "Fokus auf B2-Zeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Warum Lebenslauf?" },
          { num: "2", label: "Tabellarischer Aufbau" },
          { num: "3", label: "Persönliche Daten" },
          { num: "4", label: "Schulbildung & Erfahrung" },
          { num: "5", label: "Kenntnisse & Fähigkeiten" },
          { num: "6", label: "Layout & Design" },
          { num: "7", label: "Fehler vermeiden" },
          { num: "8", label: "Keyword & ATS" },
          { num: "9", label: "Beispiel Lebenslauf" },
          { num: "10", label: "Fazit" },
        ]}
      />

      {/* 01 */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="❓"
        title="Warum ist der Lebenslauf wichtig?"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm font-semibold text-gray-600 mb-3">
          Erster Eindruck für den Ausbildungsbetrieb
        </p>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <StatCard
            value="10–20s"
            label="Zeit pro Lebenslauf"
            icon="⏱️"
            color="bg-blue-50 border-blue-200 text-blue-800"
          />
          <StatCard
            value="1"
            label="Seite max."
            icon="📄"
            color="bg-green-50 border-green-200 text-green-800"
          />
          <StatCard
            value="↑"
            label="Höhere Chancen"
            icon="📈"
            color="bg-purple-50 border-purple-200 text-purple-800"
          />
        </div>
        <Checklist
          items={[
            <>
              Zeigt deine <strong>Qualifikationen und Stärken</strong>
            </>,
            <>
              Entscheidet oft über{" "}
              <strong>Einladung zum Vorstellungsgespräch</strong>
            </>,
            <>
              Recruiter verbringen durchschnittlich{" "}
              <strong>10–20 Sekunden</strong> mit einem Lebenslauf
            </>,
            <>
              Ein gut strukturierter Lebenslauf{" "}
              <strong>erhöht deutlich deine Chancen</strong>
            </>,
          ]}
        />
      </Accordion>

      {/* 02 */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="📊"
        title="Was ist ein tabellarischer Lebenslauf?"
        openId={openSection}
        onToggle={toggle}
      >
        <Checklist
          items={[
            <>
              <strong>Übersichtliche Darstellung in Tabellenform</strong> —
              klare Struktur auf einen Blick
            </>,
            <>
              <strong>Chronologisch</strong> — das Neueste zuerst
              (antichronologisch)
            </>,
            <>
              <strong>Standardform in Deutschland</strong> — das erwarten alle
              Betriebe
            </>,
          ]}
        />
        <InfoBox variant="tip" icon="💡" title="Merksatz">
          Das Neueste kommt zuerst. Dein letzter Schulabschluss steht oben, der
          erste unten.
        </InfoBox>
      </Accordion>

      {/* 03 */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="👤"
        title="Persönliche Daten"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Diese Angaben gehören immer an den Anfang deines Lebenslaufs:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { icon: "👤", label: "Vorname & Nachname" },
            { icon: "🏠", label: "Anschrift (Adresse)" },
            { icon: "📞", label: "Telefonnummer" },
            { icon: "✉️", label: "E-Mail Adresse" },
            { icon: "🎂", label: "Geburtsdatum" },
            { icon: "📍", label: "Geburtsort" },
            { icon: "💍", label: "Familienstand" },
            { icon: "🌍", label: "Staatsangehörigkeit" },
            { icon: "🎯", label: "Hobbys und Interessen" },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700"
            >
              <span className="text-base flex-shrink-0">{item.icon}</span>
              <span className="font-medium">{item.label}</span>
            </div>
          ))}
        </div>
        <InfoBox variant="tip" icon="📸" title="Bewerbungsfoto">
          In Deutschland ist ein professionelles Bewerbungsfoto oben rechts
          üblich (aber nicht Pflicht). Nutze dein Foto aus Lektion 01.
        </InfoBox>
      </Accordion>

      {/* 04 */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="🎓"
        title="Schulbildung & Erfahrungen"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="mb-4">
          <p className="text-sm font-bold text-gray-700 mb-2">
            💼 Arbeitserfahrung{" "}
            <span className="font-normal text-gray-400">(mit Zeitraum)</span>
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
            {[
              "Name der Firma",
              "Zeitraum (z.B. 03/2022 – 08/2022)",
              "Verantwortung / Aufgaben",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm text-gray-600"
              >
                <span className="text-[#009fe3] font-bold flex-shrink-0 mt-0.5">
                  ›
                </span>{" "}
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <p className="text-sm font-bold text-gray-700 mb-2">
            📚 Studium / Schulabschluss{" "}
            <span className="font-normal text-gray-400">(mit Zeitraum)</span>
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
            {[
              "Name des Abschlusses und der Schule/Uni",
              "Fachrichtung",
              "Klasse / Semester",
              "Ergebnis → ins deutsche Notensystem umrechnen",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm text-gray-600"
              >
                <span className="text-[#009fe3] font-bold flex-shrink-0 mt-0.5">
                  ›
                </span>{" "}
                {item}
              </div>
            ))}
          </div>
          <a
            href="https://www.tum.de/en/studies/application/application-info-portal/grade-conversion-formula-for-grades-earned-outside-germany"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#009fe3] hover:underline font-medium"
          >
            🔗 TUM Notenumrechner für ausländische Noten
          </a>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-700 mb-2">
            🔬 Praktika, Ferienjobs oder Nebenjobs
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
            {[
              "Name der Firma",
              "Kurz beschreiben, was du gelernt / gemacht hast",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm text-gray-600"
              >
                <span className="text-[#009fe3] font-bold flex-shrink-0 mt-0.5">
                  ›
                </span>{" "}
                {item}
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 05 */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="⚡"
        title="Kenntnisse & Fähigkeiten"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="mb-4">
          <p className="text-sm font-bold text-gray-700 mb-2">
            🗣️ Sprachkenntnisse
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              {
                lang: "Deutsch",
                level: "B2 / Fließend",
                color: "bg-blue-50 border-blue-200 text-blue-800",
              },
              {
                lang: "Englisch",
                level: "Gut / Grundkenntnisse",
                color: "bg-indigo-50 border-indigo-200 text-indigo-800",
              },
            ].map((l, i) => (
              <div
                key={i}
                className={`rounded-lg border px-3 py-2.5 text-sm ${l.color}`}
              >
                <span className="font-bold">{l.lang}</span>
                <span className="text-xs ml-2 opacity-70">{l.level}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <p className="text-sm font-bold text-gray-700 mb-2">
            💻 EDV-Kenntnisse
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              "MS Word",
              "MS Excel",
              "MS PowerPoint",
              "Outlook",
              "Google Docs",
            ].map((tool, i) => (
              <span
                key={i}
                className="px-3 py-1 rounded-full bg-gray-100 border border-gray-200 text-xs font-semibold text-gray-700"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-700 mb-2">
            🎯 Fähigkeiten (Hard & Soft Skills)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs font-bold text-green-700 mb-2 uppercase tracking-wide">
                Hard Skills — zuerst
              </p>
              {[
                "Buchführung",
                "Maschinenbedienung",
                "Programmieren",
                "Buchhaltung",
              ].map((s, i) => (
                <div
                  key={i}
                  className="text-xs text-green-800 flex items-center gap-1.5 mb-1"
                >
                  <span className="text-green-500 flex-shrink-0">✓</span> {s}
                </div>
              ))}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-bold text-blue-700 mb-2 uppercase tracking-wide">
                Soft Skills — danach
              </p>
              {[
                "Teamfähigkeit",
                "Kommunikation",
                "Zuverlässigkeit",
                "Lernbereitschaft",
              ].map((s, i) => (
                <div
                  key={i}
                  className="text-xs text-blue-800 flex items-center gap-1.5 mb-1"
                >
                  <span className="text-blue-500 flex-shrink-0">✓</span> {s}
                </div>
              ))}
            </div>
          </div>
        </div>
        <InfoBox variant="warning" icon="⚠️" title="Wichtig: Ehrlich bleiben!">
          Gib nur Kenntnisse an, die du wirklich hast:{" "}
          <strong>sehr gut / gut / Grundkenntnisse</strong>. Passe Fähigkeiten
          immer an die jeweilige Stellenanzeige an.
        </InfoBox>
        <div className="mt-4 bg-[#e0f2fe] border border-[#009fe3]/30 rounded-xl p-4">
          <p className="text-xs font-bold text-[#0369a1] uppercase tracking-wide mb-2">
            📐 Die XYZ-Regel für Fähigkeiten
          </p>
          <p className="text-sm text-[#0369a1] mb-3">
            Jede Fähigkeit muss durch konkrete Beispiele belegbar sein:
          </p>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              {
                letter: "X",
                desc: "Was gemacht?",
                example: "Ich habe... optimiert",
              },
              {
                letter: "Y",
                desc: "Wie / Womit?",
                example: "...durch Excel...",
              },
              {
                letter: "Z",
                desc: "Ergebnis?",
                example: "...um 30% reduziert",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-2 sm:p-2.5 border border-blue-200"
              >
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#009fe3] text-white font-extrabold text-xs sm:text-sm flex items-center justify-center mx-auto mb-1.5">
                  {item.letter}
                </div>
                <p className="text-[10px] sm:text-[11px] font-semibold text-gray-700 leading-tight">
                  {item.desc}
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-400 italic mt-1 leading-tight">
                  {item.example}
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#0369a1] mt-3 font-medium">
            💬 Mehr dazu in der Anschreiben- und Vorstellungsgespräch-Lektion!
          </p>
        </div>
      </Accordion>

      {/* 06 */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="🚫"
        title="Buzzwords & Floskeln vermeiden"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          <strong>Buzzwords sind leere Schlagwörter ohne Beweise</strong> — sie
          machen keinen guten Eindruck.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-2">
              ✕ Vermeiden
            </p>
            <ul className="space-y-1">
              {[
                '"Ich bin sehr motiviert"',
                '"Teamplayer"',
                '"Ich lerne schnell"',
                '"Ich bin kommunikativ"',
                '"Belastbar"',
              ].map((w, i) => (
                <li
                  key={i}
                  className="text-xs text-red-700 flex items-start gap-1.5"
                >
                  <span className="flex-shrink-0">✕</span> {w}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">
              ✓ Besser so (XYZ)
            </p>
            <ul className="space-y-1">
              {[
                "Konkrete Beispiele nennen",
                "Zahlen & Ergebnisse angeben",
                "Tätigkeiten beschreiben",
                "Aus der Stellenanzeige übernehmen",
                "Beweisbar formulieren",
              ].map((w, i) => (
                <li
                  key={i}
                  className="text-xs text-green-700 flex items-start gap-1.5"
                >
                  <span className="flex-shrink-0">✓</span> {w}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <InfoBox variant="info">
          Recruiter bevorzugen <strong>konkrete Informationen</strong>. Floskeln
          wirken austauschbar und unglaubwürdig.
        </InfoBox>
      </Accordion>

      {/* 07 */}
      <Accordion
        id="07"
        num="ABSCHNITT 07"
        icon="🎨"
        title="Layout & Design"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm font-semibold text-gray-700 mb-3">
          Übersichtlich und <strong>1 Seite</strong> — nicht mehr!
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            {
              icon: "🔤",
              title: "Klare Schrift",
              desc: "Arial oder Calibri, Größe 10–12pt",
            },
            {
              icon: "📐",
              title: "Einheitliches Layout",
              desc: "Gleiche Abstände überall",
            },
            {
              icon: "🖤",
              title: "Kein buntes Design",
              desc: "Schwarz/Weiß oder dezente Farben",
            },
            {
              icon: "📄",
              title: "Max. 1 Seite",
              desc: "Kurz und prägnant halten",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-2.5 p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <span className="text-xl flex-shrink-0">{item.icon}</span>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {item.title}
                </p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 08 */}
      <Accordion
        id="08"
        num="ABSCHNITT 08"
        icon="❌"
        title="Fehler vermeiden"
        openId={openSection}
        onToggle={toggle}
      >
        <ul className="list-none space-y-0">
          <DoItem type="dont">
            <strong>Rechtschreib- und Grammatikfehler</strong> — lass den
            Lebenslauf immer von G2G Korrekturlesen
          </DoItem>
          <DoItem type="dont">
            <strong>Unvollständige Angaben</strong> — alle Pflichtfelder
            ausfüllen, keine Lücken ohne Erklärung
          </DoItem>
          <DoItem type="dont">
            <strong>Unprofessionelles Foto</strong> — kein Selfie, kein
            Urlaubsfoto, kein schlechtes Licht
          </DoItem>
          <DoItem type="dont">
            <strong>Unübersichtliches Layout</strong> — zu viele Farben, zu
            viele Schriften, chaotische Struktur
          </DoItem>
          <DoItem type="do">
            <strong>Immer von G2G Korrekturlesen lassen</strong> — vor dem
            Absenden!
          </DoItem>
          <DoItem type="do">
            <strong>An jede Stelle anpassen</strong> — Keywords aus der
            Stellenanzeige einbauen
          </DoItem>
        </ul>
      </Accordion>

      {/* 09 */}
      <Accordion
        id="09"
        num="ABSCHNITT 09"
        icon="🤖"
        title="Keyword Matching & ATS"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="bg-gray-900 rounded-xl p-3 sm:p-4 mb-4 text-green-400 font-mono text-xs overflow-x-auto">
          <div className="text-gray-500 mb-2">
            // ATS = Applicant Tracking System
          </div>
          <div>scan(lebenslauf);</div>
          <div className="text-yellow-400 mt-1">{`if (!hasKeywords(stellenanzeige)) {`}</div>
          <div className="pl-4 text-red-400">{`reject(); // aussortiert ❌`}</div>
          <div className="text-yellow-400">{`} else {`}</div>
          <div className="pl-4 text-green-400">{`forward_to_recruiter(); // ✓`}</div>
          <div className="text-yellow-400">{`}`}</div>
        </div>
        <Checklist
          items={[
            <>
              Viele Betriebe nutzen{" "}
              <strong>ATS (Bewerbermanagement-Systeme)</strong>
            </>,
            <>
              Lebensläufe werden{" "}
              <strong>automatisch nach Keywords durchsucht</strong>
            </>,
            <>
              Keywords stehen meist <strong>in der Stellenanzeige</strong>
            </>,
            <>
              Fehlen wichtige Begriffe, wird der Lebenslauf oft{" "}
              <strong>aussortiert — ohne dass ein Mensch ihn liest</strong>
            </>,
          ]}
        />
        <div className="mt-4 rounded-xl border border-[#009fe3]/30 bg-blue-50 p-4">
          <p className="text-xs font-bold text-[#0369a1] uppercase tracking-wide mb-3">
            ✅ ATS-Tipps für Ausbildungssuchende
          </p>
          <div className="space-y-2">
            {[
              { icon: "🔍", tip: "Stellenanzeige genau lesen" },
              {
                icon: "🔁",
                tip: "Wichtige Begriffe aus der Anzeige im Lebenslauf wiederverwenden",
              },
              { icon: "✅", tip: "Kenntnisse klar und ehrlich auflisten" },
              {
                icon: "📋",
                tip: "Einfaches Layout verwenden (ATS mag keine Tabellen und Grafiken)",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2.5 text-sm text-[#0369a1]"
              >
                <span className="text-base flex-shrink-0">{item.icon}</span>
                <span>{item.tip}</span>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 10 - Beispiel */}
      <Accordion
        id="10"
        num="ABSCHNITT 10"
        icon="📝"
        title="Beispiel — Tabellarischer Lebenslauf"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          So sieht ein typischer tabellarischer Lebenslauf für eine
          Ausbildungsbewerbung aus:
        </p>
        <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
          <div className="bg-[#009fe3] px-4 py-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-extrabold text-white leading-tight">
                Max Mustermann
              </h3>
              <p className="text-blue-100 text-xs sm:text-sm mt-0.5">
                Bewerber für Ausbildung zum Kaufmann im Einzelhandel
              </p>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-white/20 border-2 border-white/50 flex items-center justify-center text-white text-xl flex-shrink-0">
              📸
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mb-3 pb-3 border-b border-gray-100">
              <span>📍 Musterstraße 1, 10115 Berlin</span>
              <span>📞 +49 123 456789</span>
              <span>✉️ max.mustermann@gmail.com</span>
              <span>🎂 01.01.2000 | Dhaka</span>
              <span>🌍 Bangladeschisch</span>
            </div>
            <CVSection title="Arbeitserfahrung" left="2022–2023">
              <strong>Praktikum – Supermarkt XYZ, Berlin</strong>
              <br />
              Kundenberatung, Kassenarbeit, Wareneingang und -verräumung
            </CVSection>
            <CVSection
              title="Bachelor of Business Administration"
              left="2019–2022"
            >
              <strong>Universität Dhaka, Bangladesch</strong>
              <br />
              Betriebswirtschaft | GPA 3,8 → dt. Note: <strong>1,5</strong>
            </CVSection>
            <CVSection
              title="Higher Secondary Certificate (HSC)"
              left="2017–2019"
            >
              <strong>Dhaka College, Dhaka</strong>
              <br />
              Business Studies | GPA 4,92 → dt. Note: <strong>1,3</strong>
            </CVSection>
            <CVSection
              title="Secondary School Certificate (SSC)"
              left="2015–2017"
            >
              <strong>Govt. High School, Dhaka</strong>
              <br />
              Business Studies | GPA 5,00 → dt. Note: <strong>1,0</strong>
            </CVSection>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1.5">
                    Sprachkenntnisse
                  </p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Deutsch</span>
                      <span className="font-semibold text-gray-800">B2</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Englisch</span>
                      <span className="font-semibold text-gray-800">Gut</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Bengalisch</span>
                      <span className="font-semibold text-gray-800">
                        Muttersprache
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1.5">
                    EDV-Kenntnisse
                  </p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>MS Office</span>
                      <span className="font-semibold text-gray-800">
                        Sehr gut
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Excel</span>
                      <span className="font-semibold text-gray-800">Gut</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <p className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1.5">
                  Hobbys & Interessen
                </p>
                <p className="text-xs text-gray-600">
                  Lesen, Fußball, Reisen, Kochen
                </p>
              </div>
            </div>
          </div>
        </div>
        <InfoBox variant="tip" icon="💡" title="Tipp">
          Passe alle Felder auf deine eigenen Daten an. Lass den fertigen
          Lebenslauf immer von G2G prüfen!
        </InfoBox>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800">
              Ein guter Lebenslauf ist klar, strukturiert und ehrlich.
            </p>
            <ul className="mt-2 space-y-1">
              {[
                "Passe den Lebenslauf an jede Ausbildungsstelle an",
                "Lass ihn von G2G Korrekturlesen",
                "Nutze Keywords aus der Stellenanzeige",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="✅"
        title="Deine Aufgaben – Lektion 02"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              "Lebenslauf erstellen (auf Deutsch, tabellarisch, max. 1 Seite)",
              "XYZ-Regel verstehen und für eigene Fähigkeiten anwenden",
              "3 Stellenausschreibungen auf ausbildung.de anschauen und Keywords notieren",
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
          <div className="mt-2 text-sm text-gray-600">
            <p>
              Für die dritte Aufgabe: Besuche{" "}
              <a
                href="https://www.ausbildung.de"
                target="_blank"
                rel="noreferrer"
                className="text-[#009fe3] underline"
              >
                ausbildung.de
              </a>{" "}
              und suche nach 3 interessanten Stellenausschreibungen.
            </p>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture02Lebenslauf;
